import { CapaActionStatus } from '@qualicloud/contracts';
import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  NumberInput,
  RadioGroup,
  Textarea,
} from '@/design-system';
import type { CapaActionDetail } from '@/lib/api/sgq.types';

/**
 * O DIÁRIO DE BORDO DA AÇÃO CORRETIVA.
 *
 * Morava dentro de `capa-action-detail.page.tsx`, sem teste possível: a regra
 * que ele carrega — quem pode registrar o quê — só era exercitável abrindo a
 * página inteira. Saiu para cá pelo mesmo motivo que `EffectivenessDialog` já
 * estava aqui.
 *
 * A ASSIMETRIA QUE ELE PRECISA MOSTRAR
 * Registrar no diário é de quem acompanha a ação; INICIAR e CONCLUIR são só do
 * responsável. É o que o servidor faz — a guarda em `recordExecution` é
 * `if (input.transition && action.assigneeUserId !== principal.userId)` — e é
 * segregação de funções (ISO 13485 §8.5.2): quem executou responde pelo que
 * declarou ter executado.
 *
 * A tela era MAIS restritiva que a regra e escondia o botão inteiro de quem não
 * fosse o responsável. O resultado prático: o gerente que acompanha e o colega
 * que ajudou não conseguiam registrar nada, e o diário de bordo de uma ação
 * corretiva existe justamente para receber essas vozes.
 */

export function ExecutionDialog({
  open,
  onOpenChange,
  action,
  canTransition,
  busy,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly action: CapaActionDetail;
  /**
   * Iniciar e concluir são do RESPONSÁVEL; anotar andamento é de quem enxerga a
   * ação. Sem esta distinção o diálogo ofereceria uma transição que o servidor
   * recusa com `CAPA.NOT_THE_ASSIGNEE` depois do texto escrito.
   */
  readonly canTransition: boolean;
  readonly busy: boolean;
  readonly onConfirm: (params: {
    readonly note: string;
    readonly transition: 'START' | 'COMPLETE' | null;
    readonly progressPercent?: number;
  }) => void;
}) {
  const [note, setNote] = useState('');
  const [transition, setTransition] = useState<'NONE' | 'START' | 'COMPLETE'>(
    canTransition && action.status === CapaActionStatus.PLANNED ? 'START' : 'NONE',
  );
  const [progress, setProgress] = useState(String(action.progressPercent));

  const empty = note.trim().length === 0;
  const assigneeLabel = action.assigneeName ?? 'o responsável';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return;
        onOpenChange(next);
      }}
      dismissible={!busy}
      title="Registrar execução"
      description="O diário de bordo da ação. Cada registro fica no histórico e não pode ser apagado, e pode mudar a situação da ação."
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={empty}
            onClick={() =>
              onConfirm({
                note: note.trim(),
                transition: transition === 'NONE' ? null : transition,
                progressPercent: Number(progress),
              })
            }
          >
            Registrar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="O que foi executado" required>
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={5}
            maxLength={20_000}
            showCounter
            autoFocus
          />
        </Field>

        <RadioGroup<'NONE' | 'START' | 'COMPLETE'>
          legend="Este registro muda a situação da ação?"
          value={transition}
          onValueChange={setTransition}
          options={[
            {
              value: 'NONE',
              label: 'Não — apenas anotar o andamento',
              description: 'A ação continua no estado atual.',
            },
            {
              value: 'START',
              label: 'Iniciar a ação',
              /*
               * A opção desabilitada precisa dizer POR QUE. Sem isto o não
               * responsável vê duas opções apagadas e conclui que a tela está
               * quebrada — foi assim que o beco anterior começou.
               */
              description: canTransition
                ? 'Move de Em espera para Em andamento.'
                : `Só ${assigneeLabel} inicia esta ação.`,
              disabled: !canTransition || action.status !== CapaActionStatus.PLANNED,
            },
            {
              value: 'COMPLETE',
              label: 'Concluir a ação',
              description: canTransition
                ? 'Move para Aguardando verificação de eficácia. Quem verifica é o solicitante da ação.'
                : `Só ${assigneeLabel} conclui esta ação.`,
              disabled:
                !canTransition ||
                (action.status !== CapaActionStatus.PLANNED &&
                  action.status !== CapaActionStatus.IN_PROGRESS),
            },
          ]}
        />

        <Field
          label="Percentual concluído"
          description="Acompanhamento do andamento. Não substitui a conclusão formal."
        >
          <NumberInput
            value={progress}
            onChange={(event) => setProgress(event.target.value)}
            min={0}
            max={100}
            className="w-28"
          />
        </Field>
      </div>
    </Dialog>
  );
}
